import {
  sendForgotPasswordEmail,
  submitForgotPassword,
} from '@akin-home/auth/frontend';
import { useAuthStore } from '@akin-home/auth/react';
import { ChatInputType, chat_functions } from '@akin-home/chat/frontend';
import { showErrorToast, showInfoToast } from '@akin-home/toast/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { v4 } from 'uuid';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { chatAPIOutputToTextItem } from '../../chat/ChatItem/functions';
import { ChatTextItem } from '../../chat/ChatItem/types';
import { useStateStore } from '../../stores/useStateStore';
import { NON_AUTH_CHAT_DATA } from './data';
import { processCommands } from './functions/processCommands';

export type LoginScreenState = {
  has_passed_welcome: boolean;
  /** This will be set to true if an error occured during the welcome process */
  error_in_welcome?: boolean;
  has_logged_in_before: boolean;

  waiting_for_response?: boolean;

  closeWelcome(): void;

  /** Used for resetting the state of the welcome screen */
  showWelcome(): void;

  username?: string;
  setUsername(username: string): void;

  password?: string;
  setPassword(password: string): void;

  password_mode?: LoginScreenPasswordMode;
  setPasswordMode(password_mode: LoginScreenPasswordMode): void;

  stage: LoginStage;
  goToForgotPassword(): void;
  goBackToLogin(): void;

  // Forgot password actions
  resetting_password?: boolean;
  sendResetCode(username?: string): Promise<boolean | undefined>;
  submitForgotPassword(reset_code: string, password: string): Promise<void>;

  signIn(params?: { password?: string }): Promise<void>;
  submitNewPassword(password: string): Promise<void>;
  reset(): void;
} & PreAuthChatState;

type PreAuthChatState = {
  anonymous_chat_username: string;
  session_id?: string;
  chat_item?: ChatTextItem;
  sendMessage(
    message: string,
    options?: { message_type?: ChatInputType }
  ): Promise<void>;
  sendLabel(label: string): Promise<void>;
  selectOption(params: { value: string }): Promise<void>;
};

export type LoginScreenPasswordMode = 'pin' | 'password';
export type LoginStage =
  | 'login' // The main login screen
  | 'forgot_password_enter_username'
  | 'forgot_password_enter_code'
  | 'enter_new_password'; // This is for when a password has been reset, and now needs to be updated by the user

// Create a unique anonymous chat username
const anonymous_chat_username = 'anonymous-' + v4();

export const useLoginScreenState = create<LoginScreenState>()(
  persist(
    immer((set, get) => ({
      anonymous_chat_username,

      stage: 'login',
      password_mode: 'password',

      // Disabling Onboarding. Reset to true to reactivate.
      has_passed_welcome: true,
      has_logged_in_before: false,

      setUsername(username) {
        set({ username });
      },
      setPassword(password) {
        set({ password });
      },
      setPasswordMode(password_mode) {
        set({ password_mode });
      },

      goToForgotPassword() {
        set({ stage: 'forgot_password_enter_username' });
      },
      goBackToLogin() {
        set({ stage: 'login' });
      },

      async sendResetCode(username) {
        if (!username?.length) {
          showErrorToast({ text1: 'Please enter a username' });
          return;
        }

        const result = await sendForgotPasswordEmail(username);

        if ('error' in result) {
          showErrorToast({ text1: 'Incorrect Username' });
          console.log('Error sending reset code: ', result.error);
          return;
        }

        set({ username, stage: 'forgot_password_enter_code' });
        return true;
      },

      async submitForgotPassword(reset_code, new_password) {
        const username = get().username?.trim()?.toLowerCase();

        if (!reset_code?.length) {
          showErrorToast({ text1: 'Please enter a reset code' });
          return;
        }

        if (!new_password?.length) {
          showErrorToast({ text1: 'Please enter a password' });
          return;
        }

        if (!username?.length) {
          get().goBackToLogin();
          return;
        }

        set({ resetting_password: true });

        const { error } = await submitForgotPassword(
          username,
          reset_code,
          new_password
        );

        if (error) {
          set({ resetting_password: false });
          showErrorToast({ text1: error });
          return;
        }

        await get().signIn({ password: new_password });
      },

      async submitNewPassword(password) {
        set({ resetting_password: true });

        const result = await useAuthStore
          .getState()
          .submitNewPassword(password);

        set({ resetting_password: false });

        if (result.success) {
          get().reset();
        }
      },

      async signIn(params) {
        const { username } = get();
        const password = params?.password || get().password;

        if (!username) {
          showErrorToast({ text1: 'Please enter a username' });
          return;
        }

        if (!password) {
          showErrorToast({ text1: 'Please enter a password' });
          return;
        }

        // Hook into the auth store for the actual sign in
        const result = await useAuthStore
          .getState()
          .signIn(username.trim().toLowerCase(), password);

        set({ resetting_password: false });

        if (result.error) {
          if (result.error.code === 'PasswordResetRequiredException') {
            const resetCodeSuccess = await get().sendResetCode(
              username.trim().toLowerCase()
            );

            if (resetCodeSuccess) {
              // If reset code succeeded, then send a message informing them of Aug24 AWS migration. This will be out of date after that.
              const infoMessage = `\
Akin is continually improving my performance and stability; part of this journey is that we have moved to Amazon Web Services.
As a result you need to create a new password. I have sent you a code at ${username}. Please use that to create a new password on this screen.`;
              showInfoToast({
                text1: 'Action Required - Create New Password',
                text2: infoMessage,
                visibilityTime: 20000,
                props: { style: { width: '45%' } },
              } as any);
              useStateStore.getState().speak(infoMessage);
            } else {
              // If reset code failed, then we need a safe fallback.
              const infoMessage = `Please try again in a few minutes.`;
              showErrorToast({
                text1: 'Something went wrong',
                text2: infoMessage,
              } as any);
              useStateStore
                .getState()
                .speak(`Something went wrong. ${infoMessage}`);
            }

            return;
          } else {
            showErrorToast({ text1: result.error.message });
            return;
          }
        }

        // Now the user has logged in, lets flag this
        set({ has_logged_in_before: true });

        if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
          set({ stage: 'enter_new_password' });
          return;
        }

        get().reset();
      },

      reset() {
        set({ password: '', stage: 'login', resetting_password: false });
      },

      async sendMessage(message, options) {
        const {
          anonymous_chat_username,
          username,
          session_id,
          has_logged_in_before,
        } = get();

        const message_type = options?.message_type || 'text';

        const context: any = {};

        if (username?.length) {
          context.username = username;
        }

        set({ waiting_for_response: true });

        const response = await chat_functions.sendPublicChatMessage({
          username: anonymous_chat_username,
          session_id,
          message,
          application_id: NON_AUTH_CHAT_DATA.application_id,
          message_type,
          context: {
            ...context,
            has_logged_in_before: has_logged_in_before ? 'yes' : 'no',
          },
        });

        set({ waiting_for_response: false });

        if (!response.success) {
          showErrorToast({ text1: response.error.message });
          /** If there is a chat error, make sure the user is not stuck on the welcome screen */
          get().closeWelcome();
          return;
        }

        /** If there was a failed match in the welcome screen, flag this. */
        if (!response.result.output.matched && !get().has_passed_welcome) {
          set({ error_in_welcome: true });
        }

        if (
          message_type === 'labeled_intent' &&
          !response.result.output.matched
        ) {
          // Check that it is a match for a labelled intent
          return;
        }

        const result = response.result;

        const chat_item = chatAPIOutputToTextItem(result.output);

        if (chat_item) {
          useStateStore.getState().speak(chat_item.data.text);
        }

        if (result.commands?.length) {
          processCommands({
            commands: result.commands,
            getState: get,
          });
        }

        set({
          session_id: result.session_id,
          chat_item,
        });
      },

      async sendLabel(label) {
        get().sendMessage(label, { message_type: 'labeled_intent' });
      },

      async selectOption(params) {
        const { value } = params;

        if (!value) {
          return;
        }

        // set({ chat_item: undefined });

        get().sendMessage(value);
      },

      closeWelcome() {
        set({ has_passed_welcome: true, error_in_welcome: false });
      },

      showWelcome() {
        set({ has_passed_welcome: false });
      },
    })),
    {
      name: 'login-screen-state',
      getStorage: () => AsyncStorage,

      /** This is where we select portions of state to store in persistent storage */
      partialize: (state) => ({
        has_passed_welcome: state.has_passed_welcome,
        has_logged_in_before: state.has_logged_in_before,
      }),
    }
  )
);
